/**
 * antd 主题令牌。
 *
 * 当前管理端设计令牌的单一事实来源。
 *
 * 之前 ConfigProvider 只传了 locale，没传 theme —— 于是整个管理端还是
 * antd 默认蓝 #1677ff，而移动端与小程序都是 #1F6F52 绿。同一个系统三端两个颜色，
 * 这是本轮要修的第一件事，而且改这一处就够（antd 组件全部读这些 token）。
 *
 * 管理端定位是「办公室高密度」：布置台要处理 1,314 个行组 / 2,975 行，
 * 本质是对着计划书扫一眼找异常。所以行高、字号、圆角都比移动端紧。
 */
import type { ThemeConfig } from 'antd'

/** 与移动端 styles.css、小程序 app.wxss 同一套语义与数值。 */
export const COLORS = {
  brand: '#1F6F52',
  brand2: '#2F8D69',
  ink: '#163B2F',
  sub: '#5D7A70',
  line: '#D8E7DF',
  bg: '#EEF6F2',
  card: '#FFFFFF',
  warn: '#D46B08',
  warnBg: '#FFF7E6',
  harvest: '#B7791F',
  danger: '#C0392B',
} as const

/** 编号一律等宽 —— 行号/牌号看错一位就是错株。 */
export const MONO_FAMILY =
  "'SF Mono', 'Cascadia Mono', Consolas, 'Courier New', monospace"

export const antdTheme: ThemeConfig = {
  token: {
    colorPrimary: COLORS.brand,
    colorInfo: COLORS.brand,
    colorSuccess: COLORS.brand2,
    colorWarning: COLORS.warn,
    colorError: COLORS.danger,

    colorText: COLORS.ink,
    colorTextSecondary: COLORS.sub,
    colorTextTertiary: COLORS.sub,
    colorBorder: COLORS.line,
    colorBorderSecondary: COLORS.line,
    colorBgLayout: COLORS.bg,
    colorBgContainer: COLORS.card,

    // 高密度：13px 正文 + 小圆角。表格密排时大圆角会让边界糊在一起
    fontSize: 13,
    borderRadius: 6,
    borderRadiusLG: 8,
    borderRadiusSM: 4,

    // 阴影只用一档，且只在浮层上。分隔靠边框，不靠阴影
    boxShadow: '0 2px 8px rgba(22, 59, 47, 0.08)',
    boxShadowSecondary: '0 2px 8px rgba(22, 59, 47, 0.08)',

    // 动效压到 150ms 内：管理端允许，但不做装饰性过渡
    motionDurationMid: '0.15s',
    motionDurationSlow: '0.2s',
  },
  components: {
    // 表格是布置台的主体，高密度在这里最要紧
    Table: {
      cellPaddingBlock: 6,
      cellPaddingInline: 10,
      headerBg: COLORS.bg,
      headerColor: COLORS.sub,
      rowHoverBg: COLORS.warnBg,
      borderColor: COLORS.line,
      fontSize: 13,
    },
    Card: {
      paddingLG: 12,
      // 卡片靠边框分隔，不靠阴影
      boxShadowTertiary: 'none',
    },
    Button: {
      // 32px = antd middle 默认。管理端是鼠标，不需要移动端那种 56px
      controlHeight: 32,
      fontWeight: 500,
      primaryShadow: 'none',
    },
    Input: {
      controlHeight: 32,
      activeShadow: `0 0 0 2px ${COLORS.brand2}33`,
    },
    Select: { controlHeight: 32 },
    Tag: {
      // 状态标识要「颜色块 + 文字」，标签本身不要圆到像胶囊
      borderRadiusSM: 4,
    },
    Segmented: {
      itemSelectedBg: COLORS.brand,
      itemSelectedColor: '#fff',
    },
    Drawer: { paddingLG: 16 },
    /* info 类提示用主色系浅底。antd 默认的 colorInfoBg 跟着 colorInfo 走，
       主色改成深绿后那个底色偏暗、压着文字读不轻松。 */
    Alert: {
      colorInfoBg: '#F2F8F5',
      colorInfoBorder: COLORS.line,
      colorWarningBg: COLORS.warnBg,
      withDescriptionPadding: 12,
    },
    Menu: {
      itemSelectedBg: COLORS.warnBg,
      itemSelectedColor: COLORS.brand,
      itemHeight: 36,
    },
  },
}
